import {
  startOfWeek,
  addWeeks,
  addDays,
  lastDayOfMonth,
  format,
  parse,
  isValid,
} from "date-fns";

async function separateTasksByStatus(data) {
  const result = [];
  const statusSet = new Set(); // Track unique statuses added to the result

  // Define all the status groups upfront to avoid duplication
  const taskStatusGroups = {
    3: [],
    4: [],
    2: [],
    7: [],
    1: [],
    5: [],
    6: [],
  };

  // Loop through each taskTodo in subTask
  data?.forEach((task) => {
    // If status is not defined, assign 'un_assigned'
    const status = task.attributes?.status_numeric || "un_assigned"; // Default to 'un_assigned' if no status is defined

    // Only add task to the corresponding group if the status is valid
    if (taskStatusGroups[status]) {
      taskStatusGroups[status].push(task);
    }
  });

  // Add the status groups to the result, but only if they have tasks
  Object.keys(taskStatusGroups).forEach((status) => {
    if (taskStatusGroups[status].length > 0 && !statusSet.has(status)) {
      result.push({
        status: status,
        list: taskStatusGroups[status],
      });
      // Mark this status as added to avoid duplicates
      statusSet.add(status);
    }
  });

  return result;
}

// Function to sort the grouped tasks based on priority status
async function sortTasksByPriorityStatus(groupedTasks, priorityStatus) {
  // Find the index of the priorityStatus in the grouped tasks
  const priorityStatusIndex = groupedTasks.findIndex(
    (group) => group.status === priorityStatus,
  );

  if (priorityStatusIndex !== -1) {
    // Move the priority status group to the beginning of the array
    const [priorityGroup] = groupedTasks.splice(priorityStatusIndex, 1);
    groupedTasks.unshift(priorityGroup);
  }

  return groupedTasks;
}

const formatDateTime = (date) => {
  let formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const dateParts = formatter.formatToParts(date);

  const month = dateParts[2].value;
  const year = dateParts[4].value;
  const day = dateParts[0].value;

  const hour = dateParts[6].value;
  const minute = dateParts[8].value;
  const dayPeriod = dateParts[10].value;

  return `${hour}:${minute} ${dayPeriod.toUpperCase()}, ${month} ${day}, ${year} `;
};

function formatDateIntoMonthDayYear(dateString) {
  if (!dateString) return `NA`;
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(dateString);
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day} ${year}`;
}

function getMembersWithAttributes(data) {
  const result = [];

  data?.forEach((team) => {
    const teamName = team.attributes.name; // Team name from team.attributes.name
    const members = team.included.members;

    members.forEach((member) => {
      const email = member?.attributes?.email;

      // Check if a member with the same email already exists
      const existingMember = result.find(
        (existing) => existing.email === email,
      );

      if (existingMember) {
        // If the member already exists, update the tag array with the new team name
        if (!existingMember.tag.includes(teamName)) {
          existingMember.tag.push(teamName);
        }

        // Update all the previously pushed entries to include the current team name
        result.forEach((entry) => {
          if (entry.email === email && !entry.tag.includes(teamName)) {
            entry.tag.push(teamName);
          }
        });
      } else {
        // If the member does not exist, add it to the result
        const memberData = {
          label: member.attributes.name, // Member name as label
          value: member.id, // Member id as value
          group: teamName, // Group (team) name for each member
          email: member?.attributes?.email,
          role: member?.attributes?.role,
          title: member?.attributes?.title,
          tag: [teamName], // Initialize the tag array with the current team name,
          license_status: member?.attributes?.license_status,
        };

        result.push(memberData);
      }
    });
  });

  return result; // Return the flattened result as a single array
}

function formatDateToDDMMYYYY(dateInput) {
  if (!dateInput) return "";

  const date = new Date(dateInput);

  // Check if the input is a valid date
  if (isNaN(date.getTime())) {
    return `Not Valid `;
  }

  const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

function formatDateByInputType(dateInput, inputType) {
  if (!dateInput) return ""; // Return empty string for invalid input

  const date = new Date(dateInput);

  // Check if the input is a valid date
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
  const day = String(date.getDate()).padStart(2, "0"); // Ensure 2 digits
  const hour = String(date.getHours()).padStart(2, "0"); // Ensure 2 digits
  const minute = String(date.getMinutes()).padStart(2, "0"); // Ensure 2 digits
  const week = String(getWeekNumber(date)).padStart(2, "0"); // Week number

  switch (inputType) {
    case "date":
      return `${year}-${month}-${day}`; // Format for <input type="date">
    case "datetime-local":
      return `${year}-${month}-${day}T${hour}:${minute}`; // Format for <input type="datetime-local">
    case "time":
      return `${hour}:${minute}`; // Format for <input type="time">
    case "month":
      return `${year}-${month}`; // Format for <input type="month">
    case "week":
      return `${year}-W${week}`; // Format for <input type="week">
    default:
      return `${year}-${month}-${day}`; // Format for <input type="date">
  }
}

function getWeekNumber(date) {
  // var target = new Date(date.valueOf());
  // var dayNr = (date.getDay() + 6) % 7;
  // target.setDate(target.getDate() - dayNr + 3);
  // var firstThursday = target.valueOf();
  // target.setMonth(0, 1);
  // if (target.getDay() != 4) {
  //   target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
  // }
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = date - start;
  return Math.ceil((diff / 86400000 + start.getDay() + 1) / 7);
}

function groupTasksByDay(data) {
  const tasks = [
    {
      day: "Mon",
      subTasks: [],
    },
    {
      day: "Tue",
      subTasks: [],
    },
    {
      day: "Wed",
      subTasks: [],
    },
    {
      day: "Thu",
      subTasks: [],
    },
    {
      day: "Fri",
      subTasks: [],
    },
    {
      day: "Sat",
      subTasks: [],
    },
        {
      day: "Sun",
      subTasks: [],
    },
  ];

  data.forEach((item) => {
    const date = new Date(item.attributes.end_date);
    const formatter = new Intl.DateTimeFormat("en-US", { weekday: "short" });
    const dateParts = formatter.formatToParts(date);
    const weekdayPart = dateParts.find(
      (part) => part.type === "weekday",
    )?.value;

    tasks.forEach((task) => {
      if (task.day == weekdayPart) {
        task.subTasks.push(item);
      }
    });
  });

  return tasks;
}

function convertBytes(bytes) {
  if (bytes < 1024) {
    return `${bytes} bytes`; // less than 1 KB
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`; // less than 1 MB
  } else if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`; // less than 1 GB
  } else {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`; // 1 GB or more
  }
}

/** returns the start and end date of the week in array format [2-11-2023, 7-11-2023]  */
function getFirstAndLastDayOfWeek(weekNumber, year) {
  let currentYear = year;
  let firstDayOfYear = new Date(currentYear, 0, 1);
  let firstMondayOfYear = startOfWeek(firstDayOfYear, { weekStartsOn: 1 });
  let currentWeekStarts = addWeeks(firstMondayOfYear, weekNumber - 1);
  let currentWeekEnds = addDays(currentWeekStarts, 6);
  currentWeekStarts = format(currentWeekStarts, "dd-MM-yyyy");
  currentWeekEnds = format(currentWeekEnds, "dd-MM-yyyy");
  return [currentWeekStarts, currentWeekEnds];
}
/** returns the start and end date of the month in array format [2-11-2023, 7-11-2023]
 * @param {number} month
 * @param {number} year
 * @returns {array}
 */
function getFirstAndLastDayOfMonth(month, year) {
  let firstDay = new Date(`${year}-${month}-1`);
  let lastDay = lastDayOfMonth(firstDay);
  firstDay = format(firstDay, "dd-MM-yyyy");
  lastDay = format(lastDay, "dd-MM-yyyy");
  return [firstDay, lastDay];
}

function formatWeekRangeDate(weekNumber, year) {
  let firstLastDay = getFirstAndLastDayOfWeek(weekNumber, year);
  let startDate = parse(firstLastDay[0], "dd-MM-yyyy", new Date());
  let endDate = parse(firstLastDay[1], "dd-MM-yyyy", new Date());
  startDate = format(startDate, "MMM dd yyyy");
  endDate = format(endDate, "MMM dd yyyy");
  return `${startDate} - ${endDate} (Week ${weekNumber})`;
}

function checkToday(date) {
  date = new Date(date);
  if (!isValid(date)) throw new Error("Invalid Date Object");
  return format(date, "dd-MM-yyyy") == format(new Date(), "dd-MM-yyyy");
}

function checkTomorrow(date) {
  date = new Date(date);
  if (!isValid(date)) throw new Error("Invalid Date Object");
  return (
    format(date, "dd-MM-yyyy") == format(addDays(new Date(), 1), "dd-MM-yyyy")
  );
}
function transformJson(inputJson) {
  return {
    overwrite: false,
    type: inputJson?.type || "",
    name: inputJson?.attributes?.name || "",
    category: inputJson?.attributes?.category || "",
    category_numeric: inputJson?.attributes?.category_numeric || "",
    phases:
      inputJson?.included?.phases?.map((phase) => ({
        phasenumber: (phase.attributes?.number || 1) - 1, // Convert to zero-based
        phasename: phase.attributes?.name || "",
        goals:
          phase?.included?.goals?.map((goal) => ({
            name: goal?.attributes?.name || "",
            goalNumber: (goal.attributes?.number || 1) - 1, // Convert to zero-based
            description: goal.attributes?.description || "",
            inCharge: goal.attributes?.in_charge || "",
            steps:
              goal?.included?.steps?.map((step) => ({
                stepname: step.attributes?.name || "",
                step: (step.attributes?.step || 1) - 1, // Convert to zero-based
                description: step.attributes?.description || "",
                examples: step.attributes?.examples || "",
                duration: step.attributes?.duration || "",
                dependencies: step.attributes?.dependencies || "",
                pitfalls: step.attributes?.pitfalls || "",
                output: step.attributes?.output || "",
                tools: Array.isArray(step.attributes?.tools)
                  ? step.attributes.tools.join(", ")
                  : step.attributes?.tools || "",
              })) || [],
          })) || [],
      })) || [],
  };
}

// Reverse function to convert back to one-based numbering
function reverseTransformJson(transformedJson) {
  return {
    ...transformedJson,
    phases: transformedJson.phases.map((phase) => ({
      phasenumber: (phase.phasenumber || 0) + 1, // Convert back to one-based
      phasename: phase.phasename,
      goals: phase.goals.map((goal) => ({
        name: goal.name,
        goalNumber: (goal.goalNumber || 0) + 1, // Convert back to one-based
        description: goal.description,
        inCharge: goal.inCharge,
        steps: goal.steps.map((step) => ({
          stepname: step.stepname,
          step: (step.step || 0) + 1, // Convert back to one-based
          description: step.description,
          examples: step.examples,
          duration: step.duration,
          dependencies: step.dependencies,
          pitfalls: step.pitfalls,
          output: step.output,
          tools: Array.isArray(step.tools)
            ? step.tools.join(", ")
            : step.tools || "",
        })),
      })),
    })),
  };
}

function reversePriceFormat(price) {
  return (price / 100).toFixed(2);
}

export function centsToUSD(cents) {
  if (!cents) return;
  const dollars = cents / 100;
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(dollars);

  return formatted;
}

export function paiseToINR(paise) {
  if (!paise) return;
  const rupees = paise / 100;
  const formatted = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(rupees);

  return formatted;
}

const hexToRgb = (hex) => {
  let c = hex.substring(1);
  if (c.length === 3) {
    c = c
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  const bigint = parseInt(c, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
    a: 1,
  };
};

function debounce(func, delay) {
  let timeoutFun;

  return (...args) => {
    clearTimeout(timeoutFun);
    timeoutFun = setTimeout(() => func(...args), delay);
  };
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.log(err?.message);
    return false;
  }
};

function getNameChars(name) {
  if (!name) return "";

  let chars = "";
  const nameArr = name.split(" ");
  if (nameArr.length > 1) {
    chars = nameArr[0][0];
    // + nameArr[1][0];
  } else {
    chars = nameArr[0][0];
  }

  return chars;
}

const isIframe = () => window.self !== window.top;

const getGreeting = () => {
  const hours = new Date().getHours();
  if (hours < 12) return "Good Morning";
  if (hours < 17) return "Good Afternoon";
  if (hours < 21) return "Good Evening";
  return "Hi";
};

import { getCampaignStatusById } from "@/constants/statusData";

const STATUS_LOOKUP = {
  "not started": { label: "Not Started", id: 5 },
  "action required": { label: "Action Required", id: 4 },
  "in progress": { label: "Work in Progress", id: 2 },
  "work in progress": { label: "Work in Progress", id: 2 },
  active: { label: "Work in Progress", id: 2 },
  completed: { label: "Completed", id: 1 },
  done: { label: "Completed", id: 1 },
  dormant: { label: "Dormant", id: 3 },
  draft: { label: "Draft", id: 0 },
};

const normalizeKey = (value) => {
  if (!value) return "";
  return String(value).trim().toLowerCase().replace(/[_-]+/g, " ");
};

const toTitle = (value) => value.replace(/\b\w/g, (char) => char.toUpperCase());

const prepareCampaignStats = (payload = {}) => {
  const campaigns = payload?.campaigns || {};
  const entries = Object.entries(campaigns);
  let total = Number(payload?.total);

  if (!Number.isFinite(total) || total < 0) {
    total = 0;
  }

  if (!total && entries.length) {
    total = entries.reduce((sum, [, count]) => sum + Number(count || 0), 0);
  }

  const statsData = entries.map(([rawKey, rawValue]) => {
    const normalizedKey = normalizeKey(rawKey);
    const statusInfo = STATUS_LOOKUP[normalizedKey];
    const label = statusInfo?.label || toTitle(normalizedKey || "Unknown");
    const statusId = statusInfo?.id;
    const color = getCampaignStatusById(statusId)?.color || "--assigned";
    const value = Number(rawValue || 0);
    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

    return {
      name: label,
      value,
      color,
      percentage,
    };
  });

  return { statsData, totalCampaigns: total };
};

const setWithExpiry = (key, value, ttl) => {
  const now = Date.now();
  const expiry = now + ttl;
  const item = {
    value,
    expiry,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

const getWithExpiry = (key) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  let item;
  try {
    item = JSON.parse(itemStr);
  } catch {
    return null;
  }

  if (Date.now() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
};


const formatStepIdForMoreHumanReadable = (id) => {
  if(!id || typeof id !== "string") return '';
  return id.split(':').reduce((acc,item)=> acc == "" ? `${Number(item) + 1}`  :  `${acc}:${Number(item) + 1}` ,'')
}


/**
 * Formats a raw agent/executor name into a human-readable display name.
 * Handles snake_case ("newsletter_creator") and CamelCase ("NewsletterCreatorAgent").
 */
const formatDisplayName = (name) => {
  if (!name || typeof name !== "string") return "";
  // Remove trailing "Agent" suffix
  let cleaned = name.replace(/Agent$/i, "").trim();
  // Split camelCase: "NewsletterCreator" → "Newsletter Creator"
  cleaned = cleaned.replace(/([a-z])([A-Z])/g, "$1 $2");
  // Replace underscores with spaces and title-case each word
  return cleaned
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const downloadMarkdown = (content,filename = "file.md") => {
  if(!content) return false ;
  const blob = new Blob([content],{type: "text/markdown"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
} 

function stripMarkdown(md) {
  return md
    .replace(/!\[.*?\]\(.*?\)/g, "") // images
    .replace(/\[([^\]]+)\]\((.*?)\)/g, "$1") // links
    .replace(/(```[\s\S]*?```)/g, "") // code blocks
    .replace(/`([^`]*)`/g, "$1") // inline code
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold
    .replace(/(\*|_)(.*?)\1/g, "$2") // italic
    .replace(/~~(.*?)~~/g, "$1") // strike
    .replace(/^#+\s+(.*)/gm, "$1") // headings
    .replace(/^\s*[-*+]\s+/gm, "") // lists
    .replace(/^\s*\d+\.\s+/gm, "") // numbered lists
    .replace(/>\s?/g, "") // blockquotes
    .replace(/\n{2,}/g, "\n") // extra lines
    .trim();
}

function getExecutionLabel(step) {
  if (!step) return null;
  const agent = step.agent_name || step.routed_agent;
  const types = step.interaction_types || [];
  const auto = step.auto_executable || step.auto_execute;

  if (!agent) return "Follow the steps generated by AI Agents";
  if (auto && types.length === 0) return "AI Auto-Execute";
  if (types.some((t) => t.includes("edit"))) return "AI Generates → You Review";
  if (types.some((t) => t.includes("approval")))
    return "AI Generates → You Approve";
  if (types.some((t) => t.includes("research")))
    return "AI Assists → You Research";
  if (types.length > 0) return "AI Generates → You Review";
  return "AI auto generates the output";
}



export {
  separateTasksByStatus,
  sortTasksByPriorityStatus,
  groupTasksByDay,
  formatDateTime,
  formatDateIntoMonthDayYear,
  getMembersWithAttributes,
  formatDateToDDMMYYYY,
  formatDateByInputType,
  getWeekNumber,
  convertBytes,
  formatWeekRangeDate,
  getFirstAndLastDayOfMonth,
  getFirstAndLastDayOfWeek,
  checkToday,
  checkTomorrow,
  transformJson,
  reverseTransformJson,
  hexToRgb,
  reversePriceFormat,
  debounce,
  copyText,
  getNameChars,
  isIframe,
  getGreeting,
  prepareCampaignStats,
  setWithExpiry,
  getWithExpiry,
  formatStepIdForMoreHumanReadable,
  formatDisplayName,
  downloadMarkdown,
  stripMarkdown,
  getExecutionLabel
};
